// API-клиент админки.
//
// access_token держим в памяти (см. PRD §9.2). refresh_token — в HttpOnly cookie,
// которую браузер шлёт автоматически на /api/v1/auth/* (path выставляет backend).
//
// При 401 на защищённый эндпоинт делаем silent refresh и повторяем запрос один раз.
//
// Каждый запрос несёт X-Request-ID клиентской сессии — backend связывает
// этот id с записями http.request в access-логе (см. PRD §14.4).

import { getRequestId } from "../lib/log";

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  role: "admin" | "manager";
  // PRD §10.1 — UI использует, чтобы показать в шапке предупреждение
  // менеджеру без маппинга Bitrix.
  bitrix_user_id: string | null;
}

export interface TokenPair {
  access_token: string;
  access_expires_at: string;
  refresh_token: string | null;
  refresh_expires_at: string;
  user: AuthUser;
}

interface ApiErrorBody {
  error?: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Маппинг ApiError в человечный текст для UI.
//
// Бэкенд иногда отдаёт технические сообщения («Тело запроса не прошло
// валидацию» из FastAPI-RequestValidationError, см. main.py), которые
// бесполезны пользователю. Здесь приводим типовые статус-коды к понятным
// формулировкам. Для тонкого контекста (например, «отправка не найдена»
// vs «документ не найден») вызов может передать `entityLabel`.
export function humanizeApiError(
  err: unknown,
  opts: { entityLabel?: string } = {},
): string {
  if (!(err instanceof ApiError)) {
    if (err instanceof Error) return err.message;
    return "Не удалось выполнить запрос";
  }
  const entity = opts.entityLabel;
  switch (err.status) {
    case 401:
      return "Сессия истекла. Войдите снова.";
    case 403:
      return "Нет доступа к этому ресурсу.";
    case 404:
      return entity ? `${entity} не найдена.` : "Не найдено.";
    case 422:
      // `validation.failed` от FastAPI на этапе path/body-параметров —
      // обычно значит, что в URL подсунули неправильный идентификатор
      // (например, старый short_token вместо UUID). С точки зрения
      // пользователя это «такой страницы нет».
      if (err.code === "validation.failed") {
        return entity
          ? `${entity} не найдена. Возможно, ссылка устарела.`
          : "Адрес страницы некорректен. Возможно, ссылка устарела.";
      }
      return err.message || "Запрос отклонён.";
    case 429:
      return "Слишком много запросов. Подождите и повторите.";
    case 500:
    case 502:
    case 503:
    case 504:
      return "Сервер временно недоступен. Попробуйте позже.";
    default:
      return err.message || "Не удалось выполнить запрос";
  }
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";

let accessToken: string | null = null;
let onAuthChanged: ((user: AuthUser | null) => void) | null = null;

export function setAuthChangedHandler(fn: (u: AuthUser | null) => void) {
  onAuthChanged = fn;
}

export function getAccessToken(): string | null {
  return accessToken;
}

function applyTokens(pair: TokenPair) {
  accessToken = pair.access_token;
  onAuthChanged?.(pair.user);
}

function clearTokens() {
  accessToken = null;
  onAuthChanged?.(null);
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody = {};
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* ignore */
  }
  const code = body.error?.code ?? `http.${res.status}`;
  const message = body.error?.message ?? res.statusText;
  return new ApiError(res.status, code, message, body.error?.details);
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** false — не пытаться рефрешить при 401 (используется самим refresh-флоу). */
  retryOn401?: boolean;
}

async function rawFetch(path: string, opts: RequestOpts = {}): Promise<Response> {
  const headers: Record<string, string> = {
    "X-Request-ID": getRequestId(),
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  return fetch(`${API_BASE}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: "include",
    signal: opts.signal,
  });
}

export async function apiRequest<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  let res = await rawFetch(path, opts);
  if (res.status === 401 && opts.retryOn401 !== false) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await rawFetch(path, opts);
    }
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

let refreshInflight: Promise<boolean> | null = null;

export async function tryRefresh(): Promise<boolean> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    const res = await rawFetch("/auth/refresh", {
      method: "POST",
      body: {},
      retryOn401: false,
    });
    if (!res.ok) {
      clearTokens();
      return false;
    }
    const pair = (await res.json()) as TokenPair;
    applyTokens(pair);
    return true;
  })();
  try {
    return await refreshInflight;
  } finally {
    refreshInflight = null;
  }
}

// --- Auth API ---

export async function authCodeRequest(email: string): Promise<void> {
  await apiRequest("/auth/code/request", {
    method: "POST",
    body: { email },
    retryOn401: false,
  });
}

export async function authCodeVerify(email: string, code: string): Promise<TokenPair> {
  const pair = await apiRequest<TokenPair>("/auth/code/verify", {
    method: "POST",
    body: { email, code, client_kind: "admin" },
    retryOn401: false,
  });
  applyTokens(pair);
  return pair;
}

export async function authLogout(): Promise<void> {
  try {
    await apiRequest("/auth/logout", { method: "POST", body: {}, retryOn401: false });
  } finally {
    clearTokens();
  }
}
